/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Heading,
    Flex,
    Button,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {SearchIcon} from '@salesforce/retail-react-app/app/components/icons'
import {useHistory} from 'react-router-dom'
import Link from '@salesforce/retail-react-app/app/components/link'
import {fonts} from '../../theme/foundations/fonts'
import {
    pnfmessage,
    pnftitle,
    pnfsubtitle,
    pnfImageContainer,
    recommendedProductsContainer
} from './index.style'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import sizes from '../../theme/foundations/sizes'
import RecommendedProducts from '../../components/recommended-products'
import {getUrlSearchParam} from '../../utills/url'
import {useLocation} from 'react-router-dom'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useSitePreference from '../../hooks/use-site-preference'

const PageNotFound = ({fullScreen = false}) => {
    const intl = useIntl()
    const history = useHistory()
    const {res} = useServerContext()
    const location = useLocation()
    const navigate = useNavigation()

    if (res) {
        res.status(404)
    }

    // Default styles (original)
    const defaultStyles = {
        height: '100%'
    }

    // Full screen styles
    const fullScreenStyles = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        bg: 'white'
    }

    // Choose styles based on fullScreen prop
    const containerStyles = fullScreen ? fullScreenStyles : defaultStyles

    const previousSearchParams = useMemo(() => {
        const {snitizeUrlSearchParam} = getUrlSearchParam(location.search)
        return snitizeUrlSearchParam
    }, [location.search])

    const {data} = useSitePreference('suggestedForYou')
    const suggestedRecommendation = data?.suggestedForYou

    return (
        <Box
            layerStyle={fullScreen ? undefined : 'page'}
            className="page-not-found"
            {...containerStyles}
        >
            <Helmet>
                <title>
                    {intl.formatMessage({
                        defaultMessage: '404 - Page Not Found',
                        id: 'page_not_found.title.page_cant_be_found'
                    })}
                </title>
            </Helmet>
            <Flex
                width={fullScreen ? '100%' : undefined}
                justify="center"
                align="center"
                flexDirection="column"
                px={{base: 5, md: 5}}
            >
                <Heading
                    as="h1"
                    fontFamily={fonts.primary}
                    fontSize={['4xl', '5xl']}
                    mb={4}
                    align="center"
                    sx={pnftitle}
                >
                    {intl.formatMessage({
                        defaultMessage: '404',
                        id: 'page_not_found.title.page_cant_be_found_404'
                    })}
                </Heading>
                <Heading
                    as="h2"
                    fontSize={['xl', '2xl', '2xl', '3xl']}
                    mb={2}
                    align="center"
                    sx={pnfsubtitle}
                >
                    {/* ERROR - PAGE NOT FOUND */}
                    {intl.formatMessage({
                        defaultMessage: 'ERROR - PAGE NOT FOUND',
                        id: 'page_not_found.title.page_cant_be_found_error'
                    })}
                </Heading>
                <Box>
                    <Text textAlign="center" fontFamily={fonts.primary} sx={pnfmessage}>
                        {intl.formatMessage({
                            defaultMessage:
                                'You’ve reached tumbleweed territory—time to head back.',
                            id: 'page_not_found.message.suggestion_head_back'
                        })}
                    </Text>
                </Box>
                <Stack direction={['column', 'row']}>
                    <Button
                        variant="outline"
                        bg="white"
                        onClick={() => navigate('/')}
                        borderColor={'gray.200'}
                        className="button-tertiary"
                        height={['35px', '40px']}
                        alignItems="center"
                    >
                        {intl.formatMessage({
                            defaultMessage: 'home page >>',
                            id: 'page_not_found.action.go_back_home_page'
                        })}
                    </Button>
                    <Button
                        as={Link}
                        className="button-primary"
                        to={'/'}
                        _hover={{
                            textDecoration: 'none'
                        }}
                        height={['35px', '40px']}
                        alignItems="center"
                    >
                        {intl.formatMessage({
                            defaultMessage: 'start shopping >>',
                            id: 'page_not_found.link.start_shopping'
                        })}
                    </Button>
                </Stack>
            </Flex>
            {/* image */}
            <Box width="100%" sx={pnfImageContainer}>
                <picture>
                    <source
                        srcSet={getAssetUrl('static/img/TumbleweedandWagon.gif')}
                        media={`(min-width: ${sizes.container.lg})`}
                    />
                    <source
                        srcSet={getAssetUrl('static/img/TumbleweedandWagon-mob.gif')}
                        media={`(min-width: ${sizes.container.sm})`}
                    />
                    <img
                        src={getAssetUrl('static/img/TumbleweedandWagon-mob.gif')}
                        alt="Tumbleweed and Wagon"
                    />
                </picture>
            </Box>
            <Flex
                width={fullScreen ? '100%' : undefined}
                justify="center"
                align="center"
                flexDirection="column"
            >
                {/* Product Recommendations */}
                <Stack mt={10} width="100%" sx={recommendedProductsContainer}>
                    <RecommendedProducts
                        title={suggestedRecommendation}
                        recommender="best-sellers-null-results"
                        previousSearchParams={previousSearchParams}
                        showQuickView={false}
                    />
                </Stack>
            </Flex>
        </Box>
    )
}

PageNotFound.propTypes = {
    fullScreen: PropTypes.bool
}

export default PageNotFound
